import type { TooltipProps } from "@mui/material";

/** MUI Tooltip's own trigger timings, so a lazy tooltip opens and closes on the same beat as an eager one. */
export const MUI_TOOLTIP_TIMING = { ENTER_DELAY: 100, LEAVE_DELAY: 0, ENTER_TOUCH_DELAY: 700, LEAVE_TOUCH_DELAY: 1500 } as const;

/**
 * The Tooltip props a lazy tooltip honours. It runs the standard triggers itself (hover, keyboard focus, long
 * press), so anything that changes how those work — `followCursor`, `describeChild`, the `disable*Listener`s,
 * `enterNextDelay` — or that MUI would spread onto the anchor (`className`, `style`, a `data-*`) isn't on the list.
 */
const LAZY_TOOLTIP_PROPS: ReadonlySet<string> = new Set([
	"title",
	"placement",
	"arrow",
	"enterDelay",
	"leaveDelay",
	"enterTouchDelay",
	"leaveTouchDelay",
	"disableInteractive",
	"slotProps",
	"slots",
	"classes",
	"id",
]);

/** Whether a tooltip with these props can be lazy. A popper slot given as a function can't take the anchor. */
export const supportsLazyMount = (props: Omit<TooltipProps, "children">): boolean =>
	Object.keys(props).every((key) => LAZY_TOOLTIP_PROPS.has(key)) && typeof props.slotProps?.popper !== "function";
